import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import LoginCarousel from './LoginCarousel';
import { clientClaimService } from '../../_services/clientClaim.service';
import './auth.css';
import './findMyBookings.css';

/**
 * /find-my-bookings — a plain email-verification page (beu-be docs/client-flows.md §7.4).
 *
 * Kept only so old links still work: nothing in the app links here any more.
 * Salon records under a verified email sync in silently on the server; this
 * page never mentions bookings, salons or claiming, and every outcome
 * (merged, flagged for Support, nothing found) just lets them in.
 *
 * Signed in:  send code → code → in (the code goes to the account's own email).
 * Signed out: email → code → name + password → in.
 *
 * Query: ?email= prefills (signed out); &send=1 sends the code straight away;
 * &sent=1 means a code is already on its way.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class FindMyBookings extends Component {
  constructor(props) {
    super(props);
    const q = new URLSearchParams((props.location && props.location.search) || window.location.search);
    const st = (props.location && props.location.state) || {};
    this.autoSend = q.get('send') === '1';
    this.alreadySent = q.get('sent') === '1';     // signup already sent the code
    // Arriving with an email (login prompt / signup) is about THAT email:
    // never borrow a session this browser happens to hold for someone else.
    this.forEmail = !!q.get('email');
    this.state = {
      step: 'loading',            // loading | disabled | intro | email | code | account
      signedIn: false,
      email: q.get('email') || '',
      code: '',
      first_name: st.first_name || '',
      last_name: st.last_name || '',
      password: '',
      confirmPassword: '',
      claimToken: '',
      error: '',
      info: '',
      busy: false
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const hasToken = !!localStorage.getItem('accessToken') && !this.forEmail;
    clientClaimService.status(hasToken).then((r) => {
      if (!r.ok && hasToken) {
        // Stale or foreign session: carry on as signed out.
        return clientClaimService.status(false).then((r2) => this.afterStatus(r2, false));
      }
      return this.afterStatus(r, hasToken);
    });
  }

  afterStatus(r, signedIn) {
    if (!r.ok || !r.data.enabled) {
      this.setState({ step: 'disabled' });
      return;
    }
    if (signedIn) {
      this.setState({ signedIn: true, step: 'intro' });
      return;
    }
    const validEmail = EMAIL_RE.test(this.state.email.trim());
    if (this.alreadySent && validEmail) {
      this.setState({ signedIn: false, step: 'code' });
      return;
    }
    this.setState({ signedIn: false, step: 'email' }, () => {
      if (this.autoSend && validEmail) this.sendCode();
    });
  }

  set = (e) => this.setState({ [e.target.name]: e.target.value, error: '' });

  sendCode = (e) => {
    if (e) e.preventDefault();
    const { signedIn } = this.state;
    const email = this.state.email.trim();
    if (!signedIn && !EMAIL_RE.test(email)) {
      this.setState({ error: 'Please enter a valid email address' });
      return;
    }
    this.setState({ busy: true, error: '', info: '' });
    clientClaimService.sendCode(email, signedIn).then((r) => {
      if (!r.ok) {
        this.setState({ busy: false, error: r.message });
        return;
      }
      this.setState({ busy: false, step: 'code', code: '', info: r.data.message || '' });
    });
  };

  verify = (e) => {
    if (e) e.preventDefault();
    const code = this.state.code.replace(/\D/g, '');
    if (code.length !== 6) {
      this.setState({ error: 'Enter the 6-digit code from the email' });
      return;
    }
    const { signedIn } = this.state;
    this.setState({ busy: true, error: '' });
    const params = signedIn ? { code } : { email: this.state.email.trim(), code };
    clientClaimService.verify(params, signedIn).then((r) => {
      if (!r.ok) {
        this.setState({ busy: false, error: r.message });
        return;
      }
      if (signedIn) {
        this.goToApp();
        return;
      }
      this.setState({ busy: false, step: 'account', claimToken: r.data.claimToken });
    });
  };

  complete = (e) => {
    if (e) e.preventDefault();
    const { first_name, last_name, password, confirmPassword, claimToken } = this.state;
    if (!first_name.trim()) { this.setState({ error: 'Please enter your first name' }); return; }
    if (password.length < 6) { this.setState({ error: 'Your password needs at least 6 characters' }); return; }
    if (password !== confirmPassword) { this.setState({ error: "The passwords don't match" }); return; }
    this.setState({ busy: true, error: '' });
    clientClaimService.complete({
      email: this.state.email.trim(), claimToken, password,
      first_name: first_name.trim(), last_name: last_name.trim()
    }).then((r) => {
      if (!r.ok || !r.data.accessToken) {
        this.setState({ busy: false, error: r.message });
        return;
      }
      // A fresh identity: drop whatever session this browser held (as the
      // magic-link landing does) and start from the new login.
      localStorage.clear();
      localStorage.setItem('accessToken', r.data.accessToken);
      if (r.data.refreshToken) localStorage.setItem('refreshToken', r.data.refreshToken);
      if (r.data.userId) localStorage.setItem('userId', String(r.data.userId));
      localStorage.setItem('defaultRedirectTo', '0');
      this.goToApp();
    });
  };

  goToApp = () => {
    // Full load so the app boots as the signed-in client.
    window.location.replace('/dashboard/bookings');
  };

  renderBody() {
    const { step, signedIn, email, code, first_name, last_name, password, confirmPassword, error, info, busy } = this.state;
    const err = error ? <div className="auth-error auth-mb-md" role="alert"><span>{error}</span></div> : null;
    const btn = (label) => (
      <button type="submit" className={`auth-button auth-button-primary ${busy ? 'auth-button-loading' : ''}`} disabled={busy}>{label}</button>
    );

    if (step === 'loading') return <div className="fmb-loading" aria-busy="true">Loading…</div>;
    if (step === 'disabled') {
      return (
        <>
          <h1 className="auth-title">Verify your email</h1>
          <p className="auth-subtitle">This page isn't available right now. Please sign in.</p>
          <Link to="/login" className="auth-button auth-button-primary fmb-linkbtn">Sign in</Link>
        </>
      );
    }
    if (step === 'intro') {
      return (
        <>
          <h1 className="auth-title">Verify your email</h1>
          <p className="auth-subtitle">We'll send a 6-digit code to the email on your account.</p>
          {err}
          <form className="auth-form" onSubmit={this.sendCode}>{btn('Send code')}</form>
        </>
      );
    }

    if (step === 'email') {
      return (
        <>
          <h1 className="auth-title">Verify your email</h1>
          <p className="auth-subtitle">Enter your email and we'll send you a 6-digit code.</p>
          {err}
          <form className="auth-form" onSubmit={this.sendCode}>
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="fmb-email">Email Address</label>
              <input id="fmb-email" type="email" name="email" className="auth-input" placeholder="Enter your email"
                value={email} onChange={this.set} autoComplete="email" autoFocus />
            </div>
            {btn('Send code')}
          </form>
          <div className="auth-footer-link"><span>Already have an account? </span><Link to="/login" className="auth-link">Sign in</Link></div>
        </>
      );
    }

    if (step === 'code') {
      return (
        <>
          <h1 className="auth-title">Check your email</h1>
          <p className="auth-subtitle">
            {!signedIn && email
              ? <>Enter the 6-digit code we sent to <strong>{email.trim()}</strong>.</>
              : (info || 'Enter the 6-digit code we sent to your email.')}
          </p>
          {err}
          <form className="auth-form" onSubmit={this.verify}>
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="fmb-code">Verification code</label>
              <input id="fmb-code" name="code" className="auth-input fmb-code" inputMode="numeric" autoComplete="one-time-code"
                maxLength={6} placeholder="123456" value={code}
                onChange={(e) => this.setState({ code: e.target.value.replace(/\D/g, '').slice(0, 6), error: '' })} autoFocus />
            </div>
            {btn('Verify')}
          </form>
          <div className="auth-text-center fmb-resend">
            <button type="button" className="auth-link auth-link-muted fmb-textbtn" onClick={() => this.sendCode()} disabled={busy}>Send a new code</button>
          </div>
        </>
      );
    }

    // step === 'account'
    return (
      <>
        <h1 className="auth-title">Finish signing up</h1>
        <p className="auth-subtitle">Choose a password for your Beu account.</p>
        {err}
        <form className="auth-form" onSubmit={this.complete}>
          <div className="fmb-row">
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="fmb-first">First Name</label>
              <input id="fmb-first" name="first_name" className="auth-input" value={first_name} onChange={this.set} autoComplete="given-name" />
            </div>
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="fmb-last">Last Name</label>
              <input id="fmb-last" name="last_name" className="auth-input" value={last_name} onChange={this.set} autoComplete="family-name" />
            </div>
          </div>
          <div className="auth-form-group">
            <label className="auth-label" htmlFor="fmb-pw">Password</label>
            <input id="fmb-pw" type="password" name="password" className="auth-input" value={password} onChange={this.set} autoComplete="new-password" />
          </div>
          <div className="auth-form-group">
            <label className="auth-label" htmlFor="fmb-pw2">Confirm Password</label>
            <input id="fmb-pw2" type="password" name="confirmPassword" className="auth-input" value={confirmPassword} onChange={this.set} autoComplete="new-password" />
          </div>
          {btn('Continue')}
        </form>
      </>
    );
  }

  render() {
    return (
      <AuthLayout rightPanel={<LoginCarousel />}>
        <div className="auth-form-container fmb-page">
          <div className="auth-top-row">
            <Link to={this.state.signedIn ? '/dashboard/bookings' : '/login'} className="auth-nav-back" aria-label="Back">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <div className="auth-logo">
              <img src={require('../../assets/images/loginLogo.png')} alt="Beu Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <div className="auth-top-row-spacer" />
          </div>
          {this.renderBody()}
        </div>
      </AuthLayout>
    );
  }
}

export default FindMyBookings;
